// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Session, User } from '@supabase/supabase-js'

interface AuthContextType {
  session: Session | null
  user: User | null
  profile: any | null
  isInternal: boolean
  isBeta: boolean // true when is_beta = true — full access regardless of plan/payment
  orgPlan: string // 'free' | 'starter' | 'professional' | 'enterprise'
  isPaid: boolean // true when plan !== 'free' or is_internal
  hasPolicyBuilder: boolean // true when policy_builder_addon = true or is_internal
  isHR: boolean // true when role = 'hr'
  grDisplayId: string | null // Zero-Knowledge Identity alias e.g. "GR-7429"
  isRecovery: boolean // true when session came from a password-reset link — blocks normal routing
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<any | null>(null)
  const [isInternal, setIsInternal] = useState(false)
  const [isBeta, setIsBeta] = useState(false)
  const [orgPlan, setOrgPlan] = useState<string>('free')
  const [isPaid, setIsPaid] = useState(false)
  const [hasPolicyBuilder, setHasPolicyBuilder] = useState(false)
  const [isHR, setIsHR] = useState(false)
  const [grDisplayId, setGrDisplayId] = useState<string | null>(null)
  const [isRecovery, setIsRecovery] = useState(false)
  const [loading, setLoading] = useState(true)

  // Replace this with the unpacked extension ID from chrome://extensions
  // after loading the extension. It's a 32-char lowercase string.
  const GUARDROK_EXTENSION_ID = 'dldblbcpofdflbbhpahbokamikpakhdh'

  function pushSessionToExtension(s: Session | null) {
    const c = (window as any).chrome
    if (!c?.runtime?.sendMessage) return // extension not installed
    const payload = s?.access_token
      ? { type: 'GR_SET_SESSION', access_token: s.access_token }
      : { type: 'GR_CLEAR_SESSION' }
    try {
      c.runtime.sendMessage(GUARDROK_EXTENSION_ID, payload, () => {
        // swallow lastError if the extension isn't installed in this browser
        void c.runtime.lastError
      })
    } catch {
      /* extension not installed — ignore */
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) await fetchProfile(session.user.id)
      pushSessionToExtension(session)
      setLoading(false) // only after profile is fetched
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Keep the session so updateUser() works, but flag it so routing treats it as unauthenticated
        setIsRecovery(true)
        setSession(session)
        setUser(session?.user ?? null)
        return
      }
      if (event === 'USER_UPDATED') {
        setIsRecovery(false)
      }
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else setProfile(null)
      pushSessionToExtension(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId: string) {
    const { data } = await supabase
      .from('users')
      .select('*, organizations(plan, policy_builder_addon)')
      .eq('id', userId)
      .single()
    setProfile(data)
    const internal = data?.is_internal === true
    const beta = data?.is_beta === true
    setIsInternal(internal)
    setIsBeta(beta)
    setIsHR(data?.role === 'hr')
    setGrDisplayId(data?.gr_display_id ?? null)
    const org = data?.organizations as any
    const plan: string = org?.plan ?? 'free'
    setOrgPlan(plan)
    setIsPaid(plan !== 'free' || internal)
    setHasPolicyBuilder(org?.policy_builder_addon === true || internal)
  }

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        isInternal,
        isBeta,
        orgPlan,
        isPaid,
        hasPolicyBuilder,
        isHR,
        grDisplayId,
        isRecovery,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
